import React, { useCallback, useState } from "react";
import { Upload, X, File, Image, Video, Music, FileText } from "lucide-react";

const DragDropUpload = ({ 
  onFileSelect, 
  accept = "*/*", 
  maxSize = 50 * 1024 * 1024, // 50MB default
  currentFile = null,
  mediaType = "image",
  className = ""
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);

  const getAcceptedTypes = () => {
    switch (mediaType) {
      case "image": return "image/*";
      case "video": return "video/*";
      case "audio": return "audio/*";
      case "document": return ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx";
      default: return accept;
    }
  };

  const getIcon = () => {
    switch (mediaType) {
      case "image": return <Image className="w-12 h-12 text-gray-400" />;
      case "video": return <Video className="w-12 h-12 text-gray-400" />;
      case "audio": return <Music className="w-12 h-12 text-gray-400" />;
      case "document": return <FileText className="w-12 h-12 text-gray-400" />;
      default: return <Upload className="w-12 h-12 text-gray-400" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const validateFile = (file) => {
    setError(null);
    
    if (file.size > maxSize) {
      setError(`File too large. Maximum size is ${formatFileSize(maxSize)}`);
      return false;
    }

    // Basic type validation
    const acceptedTypes = getAcceptedTypes();
    if (acceptedTypes !== "*/*") {
      const fileType = file.type;
      const fileExt = file.name.split('.').pop().toLowerCase();
      
      if (mediaType === "image" && !fileType.startsWith("image/")) {
        setError("Please select an image file");
        return false;
      }
      if (mediaType === "video" && !fileType.startsWith("video/")) {
        setError("Please select a video file");
        return false;
      }
      if (mediaType === "audio" && !fileType.startsWith("audio/")) {
        setError("Please select an audio file");
        return false;
      }
      if (mediaType === "document") {
        const allowedExts = ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx"];
        if (!allowedExts.includes(fileExt)) {
          setError("Please select a document file (PDF, Word, Excel, or PowerPoint)");
          return false;
        }
      }
    }

    return true;
  };

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        onFileSelect(file);
      }
    }
  }, [onFileSelect, mediaType, maxSize]);

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file && validateFile(file)) {
      onFileSelect(file);
    }
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    onFileSelect(null);
    setError(null);
  };

  return (
    <div className={className}>
      <div
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 cursor-pointer
          ${isDragging 
            ? "border-primary-500 bg-primary-50" 
            : currentFile 
              ? "border-green-400 bg-green-50" 
              : "border-gray-300 hover:border-primary-400 hover:bg-gray-50"
          }
        `}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => document.getElementById(`file-input-${mediaType}`).click()}
        data-testid="drag-drop-zone"
      >
        <input
          id={`file-input-${mediaType}`}
          type="file"
          accept={getAcceptedTypes()}
          onChange={handleFileInput}
          className="hidden"
          data-testid="drag-drop-file-input"
        />

        {currentFile ? (
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-3">
              <File className="w-8 h-8 text-green-600" />
            </div>
            <p className="font-medium text-gray-900 mb-1">{currentFile.name}</p>
            <p className="text-sm text-gray-500 mb-3">{formatFileSize(currentFile.size)}</p>
            <button
              onClick={handleRemoveFile}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-100 text-red-600 text-sm font-medium hover:bg-red-200 transition-colors"
              data-testid="remove-file-btn"
            >
              <X className="w-4 h-4" />
              Remove
            </button>
          </div>
        ) : (
          <>
            <div className={`mx-auto mb-4 transition-transform ${isDragging ? "scale-110" : ""}`}>
              {getIcon()}
            </div>
            <p className="font-medium text-gray-900 mb-1">
              {isDragging ? "Drop your file here" : "Drag and drop your file here"}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              or click to browse from your computer
            </p>
            <div className="flex items-center justify-center gap-2">
              <span className="px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {mediaType === "image" && "JPG, PNG, GIF, WebP"}
                {mediaType === "video" && "MP4, WebM, MOV"}
                {mediaType === "audio" && "MP3, WAV, OGG"}
                {mediaType === "document" && "PDF, DOC, XLS, PPT"}
              </span>
              <span className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                Max {formatFileSize(maxSize)}
              </span>
            </div>
          </>
        )}

        {/* Drag overlay */}
        {isDragging && (
          <div className="absolute inset-0 bg-primary-500/10 rounded-xl flex items-center justify-center pointer-events-none">
            <div className="bg-white rounded-lg px-6 py-3 shadow-lg">
              <p className="font-medium text-primary-700">Release to upload</p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-600 flex items-center gap-1" data-testid="upload-error">
          <X className="w-4 h-4" />
          {error}
        </p>
      )}
    </div>
  );
};

export default DragDropUpload;
